import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Home, Search, HelpCircle, FileText } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Set meta tags for 404 page - prevent indexing
  usePageMeta({
    title: "404 - Page Not Found | Let's Be Real",
    description: "The page you're looking for doesn't exist. Find property insights, sample reports, FAQ, and more on Let's Be Real.",
    robots: "noindex, nofollow"
  });

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-2xl w-full space-y-8">
          {/* 404 Header */}
          <div className="text-center space-y-4">
            <h1 className="text-8xl font-bold text-primary">404</h1>
            <h2 className="text-3xl font-bold text-foreground">Page Not Found</h2>
            <p className="text-lg text-muted-foreground">
              Oops! Looks like you've wandered into uncharted territory. The page you're looking for doesn't exist.
            </p>
          </div>

          {/* Helpful Links */}
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-xl font-bold mb-4 text-foreground">Where would you like to go?</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <Button
                  onClick={() => navigate("/")}
                  variant="outline"
                  className="h-auto py-4 flex flex-col items-start gap-2"
                >
                  <div className="flex items-center gap-2">
                    <Home className="h-5 w-5" />
                    <span className="font-semibold">Home</span>
                  </div>
                  <span className="text-xs text-muted-foreground text-left">
                    Search for property insights
                  </span>
                </Button>

                <Button
                  onClick={() => navigate("/sample-reports")}
                  variant="outline"
                  className="h-auto py-4 flex flex-col items-start gap-2"
                >
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    <span className="font-semibold">Sample Reports</span>
                  </div>
                  <span className="text-xs text-muted-foreground text-left">
                    See example property reports
                  </span>
                </Button>

                <Button
                  onClick={() => navigate("/faq")}
                  variant="outline"
                  className="h-auto py-4 flex flex-col items-start gap-2"
                >
                  <div className="flex items-center gap-2">
                    <HelpCircle className="h-5 w-5" />
                    <span className="font-semibold">FAQ</span>
                  </div>
                  <span className="text-xs text-muted-foreground text-left">
                    Common questions answered
                  </span>
                </Button>

                <Button
                  onClick={() => navigate("/about")}
                  variant="outline"
                  className="h-auto py-4 flex flex-col items-start gap-2"
                >
                  <div className="flex items-center gap-2">
                    <Search className="h-5 w-5" />
                    <span className="font-semibold">About & Contact</span>
                  </div>
                  <span className="text-xs text-muted-foreground text-left">
                    Learn more about us
                  </span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Quick Info */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6">
              <h3 className="font-semibold mb-2 text-foreground">What is Let's Be Real?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                We provide brutally honest, data-driven property insights for any Australian address. 
                Get comprehensive neighborhood analysis using Census 2021 data, crime stats, transport info, and AI analysis.
              </p>
              <Button onClick={() => navigate("/")} className="w-full sm:w-auto">
                Start Searching Properties
              </Button>
            </CardContent>
          </Card>

          {/* Footer note */}
          <p className="text-center text-sm text-muted-foreground">
            Tried to access: <code className="text-xs bg-muted px-2 py-1 rounded">{location.pathname}</code>
          </p>
        </div>
    </div>
  );
};

export default NotFound;
